<template>
    <div class="modal-container">
        <div class="modal-inner">
            <div class="modal-dialog modal-dialog--basket" role="dialog" aria-modal="true">
                <button class="modal-close" @click="$emit('close')">
                    <svg xmlns="http://www.w3.org/2000/svg" height="42" viewBox="0 -960 960 960" width="42">
                        <path d="M256-213.847 213.847-256l224-224-224-224L256-746.153l224 224 224-224L746.153-704l-224 224 224 224L704-213.847l-224-224-224 224Z"/>
                    </svg>
                </button>
                <div class="modal-dialog__content">
                    <div class="basket-modal">
                        <div class="basket-modal__header">
                            <p class="basket-modal__title title title--3 weight-medium">Added to basket!</p>
                        </div>
                        <div class="basket-modal__added-product">
                            <div v-if="currentProduct" class="basket-item">
                                <div class="basket-item__product-image">
                                    <img v-if="image" :src="image.url" :width="image.width" :height="image.height" :alt="image.altText">
                                </div>
                                <div class="basket-item__product-info">
                                    <div class="basket-item__product-description">
                                        <p class="weight-medium mb-1">
                                            {{ currentProduct.name }}
                                        </p>
                                        <p v-if="matchingAttributes" class="basket-item__attributes">
                                            <span v-for="(attribute, index) of matchingAttributes" :key="`category-${attribute.alias}`">
                                                {{attribute.name}}<span v-if="index < matchingAttributes.length - 1">, </span>
                                            </span>
                                        </p>
                                        <p v-if="extras" class="basket-item__extras">
                                            <span v-for="(extra, index) of extras" :key="`category-${extra.alias}`">
                                                {{ extra.name }}<span v-if="index < extras.length - 1">, </span>
                                            </span>
                                        </p>
                                    </div>
                                    <div class="basket-item__product-totals">
                                        <div class="basket-item__quantity">
                                            <p>{{ quantity }}</p>
                                        </div>
                                        <div class="basket-item__product-controls">
                                            <p class="basket-item__price">
                                                <span class="visually-hidden">Price:</span>
                                                {{ formattedCurrency(price) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div> 
                        </div>
                        <div class="basket-modal__order-summary">
                            <div class="basket-summary">
                                <div class="basket-summary__item">
                                    <p class="text-small weight-medium">Your Basket</p> <p class="text-small weight-medium">{{ basketCount }} items</p>
                                </div>
                                <div class="basket-summary__item">
                                    <p class="text-small">Subtotal:</p> <p class="title title--6">{{ basketSummary.subtotal }}</p>
                                </div>
                                <div class="basket-summary__item">
                                    <p class="text-small">VAT:</p> <p class="title title--6">{{ basketSummary.tax }}</p>
                                </div>
                                <hr class="hr">
                                <div class="basket-summary__item">
                                    <p>Total:</p>
                                    <p class="title title--4 weight-medium">{{ basketSummary.total }}</p>
                                </div>
                                <div class="basket-summary__button button-list button-list--space-between">
                                    <button @click="$emit('close')" class="button button--green-alt">
                                        <span>Continue Shopping</span>
                                    </button>
                                    
                                    <a :href="basketUrl" class="button button--green button--has-icon">
                                        <span>View Basket</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"></path></svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="basket-modal__product-recommendations">
                        <slot></slot>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import { useProductStore } from '../../stores/_product-store.js';
    import { storeToRefs } from "pinia";

    const store = useProductStore();

    const { currentProduct, currentProductData, quantity, extras, basketSummary } = storeToRefs(store);

    const attributeGroups = currentProductData.value.attributeGroups;

    const attributeAliases = currentProduct.value.attributeAliases;

    const image = currentProductData.value.basketImage;

    const formattedCurrency = computed(() => {
        return (number) => {
            const locale = 'en-GB';
            const currency = 'GBP';
            return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(number);
        };
    });

    const basketCount = computed(() => {
        return store.getBasketCount;
    });

    const props = defineProps({
        price: Number,
        basketUrl: String
    });

    function findMatchingAttributes(data, aliases) {
        const matchingAttributes = [];

        data.forEach(category => {
            category.attributes.forEach(attribute => {
                if (aliases.includes(attribute.alias)) {
                    matchingAttributes.push(attribute);
                }
            });
        });

        return matchingAttributes;
    }

    // Find matching attributes
    const matchingAttributes = findMatchingAttributes(attributeGroups, attributeAliases);

</script>